import { useRef, useState } from 'react';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { WEEKDAYS } from '@quire/shared';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ApiError, api, type ClassDetail } from '../lib/api';
import { ErrorNote, SectionHeading, Spinner, UndoNote } from '../components/ui';
import { Related } from '../components/Related';
import { DeleteRecord } from '../components/DeleteRecord';
import { Confirm } from '../components/Confirm';
import { useIsPlatformAdmin } from '../lib/useSession';

function message(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback;
}

const today = () => new Date().toISOString().slice(0, 10);

/**
 * Everything about one class in one place: what it is marked across, who marks
 * it, when it meets, and who is on the roll.
 *
 * Periods and teachers are both edited by sending the desired final list to
 * `PATCH /admin/classes/:id`, which applies it differentially and rewrites each
 * period's sort order from its position in the array. Attaching, detaching and
 * reordering are therefore the same request, and each is atomic.
 */
export function ManageClassDetail() {
  const { classId } = useParams({ from: '/app/manage/classes/$classId' });
  const queryClient = useQueryClient();

  const detail = useQuery({
    queryKey: ['class', classId],
    queryFn: () => api.classes.detail(classId),
  });
  const periods = useQuery({ queryKey: ['periods'], queryFn: api.admin.periods });
  const teachers = useQuery({ queryKey: ['teachers'], queryFn: api.admin.teachers });

  /*
   * The register caches per date under ['grid', classId, date], and everything
   * on this page is one of its inputs: periods are its columns, the roll is its
   * rows, and the meeting days decide whether it calls itself a scheduled day.
   * None of the three invalidated it, so editing a class and then opening its
   * register showed the old shape from cache.
   *
   * That last sentence used to say the stale register was in ANOTHER TAB, which
   * was never true: each tab builds its own QueryClient in main.tsx and nothing
   * here syncs between them. The case this actually fixes is one tab navigating
   * away and back.
   *
   * ['periods'] is here because the organization's period list reports how many
   * classes mark each one, so attaching or detaching a period on this page
   * changes a number on the Classes tab. Unprefixed, because that list is not
   * per class.
   *
   * The two element keys are deliberate: prefix matching clears every cached
   * date at once, which is what is wanted, and it means no caller has to know
   * which dates happen to be cached.
   */
  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['class', classId] });
    await queryClient.invalidateQueries({ queryKey: ['classes'] });
    await queryClient.invalidateQueries({ queryKey: ['grid', classId] });
    await queryClient.invalidateQueries({ queryKey: ['periods'] });
  };

  /*
   * One mutation per section rather than one for the page.
   *
   * All three send the same request, but sharing a single one meant sharing its
   * pending flag and its error too: renaming the class greyed out the period
   * list and the staff list while it saved, and a failure anywhere printed its
   * message once at the top of the page, which on a page this long is often not
   * where you were looking.
   */
  const saveClass = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.admin.updateClass(classId, body),
    onSettled: refresh,
  });

  /*
   * Reordering is applied optimistically, because a drop that visibly springs
   * back to the old order for the length of a request reads as a failed drag.
   * The snapshot is restored if the request fails, so a real failure still
   * looks like one.
   *
   * Only the periods need this. A rename or a staff change moves nothing the
   * eye is already following, so there is nothing to spring back.
   */
  const savePeriods = useMutation({
    mutationFn: (periodIds: string[]) => api.admin.updateClass(classId, { periodIds }),
    onMutate: async (periodIds) => {
      await queryClient.cancelQueries({ queryKey: ['class', classId] });
      const previous = queryClient.getQueryData<ClassDetail>(['class', classId]);
      if (previous) {
        const byId = new Map(previous.periods.map((period) => [period.periodId, period]));
        queryClient.setQueryData<ClassDetail>(['class', classId], {
          ...previous,
          periods: periodIds
            .map((id) => byId.get(id))
            .filter((period): period is ClassDetail['periods'][number] => period !== undefined),
        });
      }
      return { previous };
    },
    onError: (_error, _periodIds, context) => {
      if (context?.previous) queryClient.setQueryData(['class', classId], context.previous);
    },
    onSettled: refresh,
  });

  const saveTeachers = useMutation({
    mutationFn: (teacherIds: string[]) => api.admin.updateClass(classId, { teacherIds }),
    onSettled: refresh,
  });

  const [periodQuery, setPeriodQuery] = useState('');
  const [periodUndo, setPeriodUndo] = useState<{ message: string; periodIds: string[] } | null>(
    null,
  );
  const periodPanel = useRef<HTMLDivElement>(null);

  /*
   * Create a period without leaving the class.
   *
   * The organization's period list stays canonical and still lives on
   * /manage/classes; this only saves the trip there and back, which was the
   * whole cost of noticing mid-setup that a column was missing.
   *
   * The attached list arrives as a variable rather than being closed over,
   * because it is derived after this component's early returns and a mutation
   * has to be declared before them.
   */
  const createPeriod = useMutation({
    mutationFn: async ({
      name,
      restore,
      attached,
    }: {
      name: string;
      restore?: string;
      attached: string[];
    }) => {
      const id = restore
        ? await api.admin.updatePeriod(restore, { isActive: true }).then(() => restore)
        : await api.admin
            .createPeriod({
              name,
              // The same expression /manage/classes uses, so the two creation
              // points cannot drift apart.
              sortOrder: periods.data?.periods.length ?? 0,
            })
            .then((period) => period.id);
      await api.admin.updateClass(classId, { periodIds: [...attached, id] });
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: ['periods'] });
      await refresh();
    },
    onSuccess: () => {
      setPeriodQuery('');
      periodPanel.current?.focus();
    },
  });

  const [staffQuery, setStaffQuery] = useState('');
  const [staffUndo, setStaffUndo] = useState<{ message: string; teacherIds: string[] } | null>(
    null,
  );

  if (detail.isLoading) return <Spinner />;
  if (detail.isError || !detail.data) {
    return (
      <div>
        <Link to="/manage/classes" className="eyebrow">
          Back to classes
        </Link>
        <div className="mt-4">
          <ErrorNote message={message(detail.error, 'Could not load this class.')} />
        </div>
      </div>
    );
  }

  const klass = detail.data;
  const attachedIds = klass.periods.map((p) => p.periodId);
  const setPeriods = (ids: string[]) => savePeriods.mutate(ids);

  const teacherIds = klass.teachers.map((t) => t.id);
  const unattached = (periods.data?.periods ?? []).filter(
    (p) => p.isActive && !attachedIds.includes(p.id),
  );
  const unassigned = (teachers.data?.teachers ?? []).filter((t) => !teacherIds.includes(t.id));

  /*
   * What a typed period name that matches nothing in the list above means.
   *
   * Restoring is not a preference here: periods_org_name_key is unique on
   * (organization_id, name) and ignores is_active, so creating a namesake of a
   * retired period is a 409 rather than a second period. Offering "create"
   * there would offer a button that always fails. It is also the right answer
   * on its own terms, because the retired period owns every mark ever taken
   * against it and a namesake would fork that history into two identical
   * columns.
   */
  const typed = periodQuery.trim();
  const sameName = (periods.data?.periods ?? []).find(
    (p) => p.name.toLowerCase() === typed.toLowerCase(),
  );
  const newPeriod =
    typed === '' || (sameName && sameName.isActive && !attachedIds.includes(sameName.id))
      ? null
      : sameName?.isActive
        ? ({ kind: 'attached' } as const)
        : sameName
          ? ({ kind: 'restore', id: sameName.id, name: sameName.name } as const)
          : ({ kind: 'create', name: typed } as const);

  return (
    <div className="grid gap-8">
      <div>
        <Link to="/manage/classes" className="eyebrow hover:text-ink">
          Back to classes
        </Link>
        <div className="mt-2">
          <ClassHeader
            klass={klass}
            onSave={(body) => saveClass.mutate(body)}
            onRetire={() => saveClass.mutateAsync({ isActive: false })}
          />
        </div>
        {saveClass.error ? (
          <div className="mt-3">
            <ErrorNote message={message(saveClass.error, 'Could not save that change.')} />
          </div>
        ) : null}
      </div>

      <Related
        title="Marked across"
        description="These are the columns of this class's register, in this order. A period removed here stays on the dates it was already marked on, so nothing recorded is lost or hidden."
        count={`${klass.periods.length} columns`}
        error={savePeriods.error ? message(savePeriods.error, 'Could not save that change.') : null}
        undo={
          periodUndo
            ? {
                message: periodUndo.message,
                onUndo: () => {
                  setPeriods(periodUndo.periodIds);
                  setPeriodUndo(null);
                },
                onDismiss: () => setPeriodUndo(null),
              }
            : null
        }
        label="Attach a period"
        options={unattached.map((p) => ({ id: p.id, label: p.name }))}
        query={periodQuery}
        onQuery={setPeriodQuery}
        empty="Every active period is already attached."
        searchPlaceholder="name, or a new one"
        alwaysSearch
        focusRef={periodPanel}
        disabled={savePeriods.isPending || createPeriod.isPending}
        footer={
          newPeriod ? (
            <li className="pt-2">
              {newPeriod.kind === 'attached' ? (
                <p className="text-sm text-ink-soft">&ldquo;{typed}&rdquo; is already attached.</p>
              ) : (
                <button
                  type="button"
                  className="btn"
                  disabled={createPeriod.isPending}
                  onClick={() =>
                    createPeriod.mutate(
                      newPeriod.kind === 'restore'
                        ? { name: newPeriod.name, restore: newPeriod.id, attached: attachedIds }
                        : { name: newPeriod.name, attached: attachedIds },
                    )
                  }
                >
                  {newPeriod.kind === 'restore'
                    ? `Restore "${newPeriod.name}"`
                    : `Create "${newPeriod.name}"`}
                </button>
              )}
              {newPeriod.kind === 'restore' ? (
                <p className="mt-1.5 text-xs text-ink-faint">
                  It exists but is retired. Restoring keeps every mark already taken against it.
                </p>
              ) : null}
            </li>
          ) : null
        }
        verb="Attach"
        applying={savePeriods.isPending}
        note={
          createPeriod.error ? (
            <div className="mt-3">
              <ErrorNote
                message={message(
                  createPeriod.error,
                  `Created "${typed}", but could not attach it. It is in the list above: tick it and try again.`,
                )}
              />
            </div>
          ) : null
        }
        onApply={(ids, done) => {
          setPeriodUndo(null);
          savePeriods.mutate([...attachedIds, ...ids], {
            onSuccess: () => {
              setPeriodQuery('');
              done();
            },
          });
        }}
      >
        {klass.periods.length === 0 ? (
          <p className="mb-4 text-sm text-ink-soft">
            No periods attached, so this class has nothing to mark yet.
          </p>
        ) : (
          <PeriodList
            periods={klass.periods}
            busy={savePeriods.isPending}
            onReorder={setPeriods}
            onRemove={async (periodId) => {
              const going = klass.periods.find((period) => period.periodId === periodId);
              await savePeriods.mutateAsync(attachedIds.filter((id) => id !== periodId));
              setPeriodUndo({
                message: `Removed ${going?.name ?? 'that period'} from this class.`,
                periodIds: attachedIds,
              });
            }}
          />
        )}
      </Related>

      <Related
        title="Taken by"
        description="Teachers assigned here can open this class's register. Administrators can already reach every class."
        count={`${klass.teachers.length} assigned`}
        error={
          saveTeachers.error ? message(saveTeachers.error, 'Could not save that change.') : null
        }
        undo={
          staffUndo
            ? {
                message: staffUndo.message,
                onUndo: () => {
                  saveTeachers.mutate(staffUndo.teacherIds);
                  setStaffUndo(null);
                },
                onDismiss: () => setStaffUndo(null),
              }
            : null
        }
        label="Assign staff"
        options={unassigned.map((t) => ({ id: t.id, label: t.name, detail: t.email }))}
        query={staffQuery}
        onQuery={setStaffQuery}
        empty="Every staff member is already assigned."
        searchPlaceholder="name or email"
        disabled={saveTeachers.isPending}
        verb="Assign"
        applying={saveTeachers.isPending}
        onApply={(ids, done) => {
          setStaffUndo(null);
          saveTeachers.mutate([...teacherIds, ...ids], {
            onSuccess: () => {
              setStaffQuery('');
              done();
            },
          });
        }}
      >
        {klass.teachers.length === 0 ? (
          <p className="mb-4 text-sm text-ink-soft">Nobody assigned yet.</p>
        ) : (
          <ul className="mb-4">
            {klass.teachers.map((teacher) => (
              <li
                key={teacher.id}
                className="flex flex-wrap items-center gap-3 border-b border-rule py-2 last:border-0"
              >
                <span className="text-sm">{teacher.name}</span>
                <span className="font-mono text-xs text-ink-faint">{teacher.email}</span>
                <span className="ml-auto">
                  <Confirm
                    trigger="remove"
                    triggerClassName="text-xs text-ink-soft underline hover:text-ink"
                    title={`Remove ${teacher.name}?`}
                    description="They can no longer open this register. Marks they made on it are kept."
                    confirmLabel="Remove"
                    pendingLabel="Removing…"
                    disabled={saveTeachers.isPending}
                    onConfirm={async () => {
                      await saveTeachers.mutateAsync(teacherIds.filter((id) => id !== teacher.id));
                      setStaffUndo({ message: `Removed ${teacher.name}.`, teacherIds });
                    }}
                    errorFallback="Could not remove them."
                  />
                </span>
              </li>
            ))}
          </ul>
        )}
      </Related>

      <div className="grid gap-8 lg:grid-cols-2">
        <MeetingDays classId={classId} />
        <Roster classId={classId} />
      </div>
    </div>
  );
}

/** The class name, editable in place, and whether it is in use. */
function ClassHeader({
  klass,
  onSave,
  onRetire,
}: {
  klass: ClassDetail;
  onSave: (body: Record<string, unknown>) => void;
  /** Asks first; the panel waits on the promise. */
  onRetire: () => Promise<unknown>;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const canRestore = useIsPlatformAdmin();
  const [name, setName] = useState(klass.name);

  /*
   * Separate from the page's saveClass: a deleted class is no longer visible to
   * the person who deleted it, so staying here would refetch into a 404.
   */
  const remove = useMutation({
    mutationFn: (isDeleted: boolean) => api.admin.updateClass(klass.id, { isDeleted }),
    onSuccess: async (_result, isDeleted) => {
      await queryClient.invalidateQueries({ queryKey: ['classes'] });
      await queryClient.invalidateQueries({ queryKey: ['class', klass.id] });
      if (isDeleted) await navigate({ to: '/manage/classes' });
    },
  });
  const dirty = name.trim() !== klass.name;

  return (
    <div className="flex flex-wrap items-end justify-between gap-4">
      <div>
        <p className="eyebrow mb-1.5">Class</p>
        <form
          className="flex flex-wrap items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (dirty) onSave({ name: name.trim() });
          }}
        >
          <input
            className="field w-full text-lg font-semibold sm:w-64"
            value={name}
            aria-label="Class name"
            onChange={(e) => setName(e.target.value)}
          />
          <button className="btn" type="submit" disabled={!dirty}>
            Rename
          </button>
        </form>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        {klass.isActive || klass.deletedAt ? null : <span className="eyebrow">retired</span>}
        {klass.deletedAt ? null : klass.isActive ? (
          <Confirm
            trigger="Retire class"
            title={`Retire ${klass.name}?`}
            description="It leaves Attendance and nobody can mark it. Its registers and marks are kept, and it can be restored."
            confirmLabel="Retire class"
            pendingLabel="Retiring…"
            onConfirm={onRetire}
            errorFallback="Could not retire this class."
          />
        ) : (
          <button type="button" className="btn" onClick={() => onSave({ isActive: true })}>
            Restore class
          </button>
        )}
        {klass.isActive ? null : (
          <DeleteRecord
            noun="class"
            markCount={klass.markCount}
            deleted={Boolean(klass.deletedAt)}
            canRestore={canRestore}
            onDelete={() => remove.mutateAsync(true)}
            onRestore={() => remove.mutate(false)}
            pending={remove.isPending}
            error={remove.error ? message(remove.error, 'Could not change that.') : null}
          />
        )}
      </div>
    </div>
  );
}

/**
 * Which weekdays a register is expected on, and over which dates.
 *
 * Seven toggles rather than a menu and an Add button, which is what let the
 * same day be added twice: a day is on or off, so a duplicate Sunday is not
 * something to validate against, it is something that cannot be expressed.
 *
 * Turning a day off ENDS it rather than deleting it. These rows are the only
 * record that a class was ever expected to meet, so deleting one says it never
 * met on Fridays, while ending it says it stopped, which is what happened.
 */
function MeetingDays({ classId }: { classId: string }) {
  const queryClient = useQueryClient();
  const [from, setFrom] = useState(today());
  const [undo, setUndo] = useState<{ message: string; id: string } | null>(null);

  const schedules = useQuery({
    queryKey: ['schedules', classId],
    queryFn: () => api.admin.schedules(classId),
  });

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: ['schedules', classId] });
    await queryClient.invalidateQueries({ queryKey: ['grid', classId] });
  };

  const add = useMutation({
    mutationFn: (weekday: number) =>
      api.admin.createSchedule({ classId, weekday, effectiveFrom: from }),
    onSuccess: invalidate,
  });

  const setEnd = useMutation({
    mutationFn: ({ id, date }: { id: string; date: string | null }) =>
      api.admin.updateSchedule(id, date),
    onSuccess: invalidate,
  });

  const rows = schedules.data?.schedules ?? [];
  /** A day is on when it has a pattern that has not been ended. */
  const openFor = (weekday: number) => rows.find((r) => r.weekday === weekday && !r.effectiveTo);

  return (
    <section>
      <SectionHeading
        title="Meets on"
        description="Which days a register is expected. This is what makes 'expected 12 Sundays, held 10' answerable, so a day that stops is ended rather than removed: the meetings it already covered stay expected."
      />

      <div className="mb-4 flex flex-wrap gap-1.5">
        {WEEKDAYS.map((name, weekday) => {
          const open = openFor(weekday);
          const chip =
            'grid size-10 place-items-center rounded border text-sm font-medium transition-colors';
          const busy = add.isPending || setEnd.isPending;
          // Turning a day off asks, with the last day in the question. Turning
          // one on is one click, as adding always is.
          return open ? (
            <Confirm
              key={name}
              trigger={name.slice(0, 2)}
              triggerClassName={chip}
              triggerLabel={name}
              triggerProps={{
                'aria-pressed': true,
                style: {
                  borderColor: 'var(--color-primary)',
                  background: 'var(--color-primary)',
                  color: 'var(--color-primary-ink)',
                },
              }}
              disabled={busy}
              align="start"
              title={`Stop meeting on ${name}s?`}
              description="The day ends rather than being removed, so the meetings it already covered stay expected."
              body={
                <label className="grid gap-1.5">
                  <span className="eyebrow">Last {name}</span>
                  <input
                    name="date"
                    type="date"
                    className="field w-auto font-mono"
                    defaultValue={today()}
                    required
                  />
                </label>
              }
              confirmLabel="End"
              pendingLabel="Ending…"
              onConfirm={async (form) => {
                const date = String(form.get('date'));
                await setEnd.mutateAsync({ id: open.id, date });
                setUndo({ message: `${name} now ends ${date}.`, id: open.id });
              }}
              errorFallback="Could not end that day."
            />
          ) : (
            <button
              key={name}
              type="button"
              aria-pressed={false}
              aria-label={name}
              className={chip}
              style={{
                borderColor: 'var(--color-rule-strong)',
                background: 'var(--color-raised)',
                color: 'var(--color-ink-soft)',
              }}
              disabled={busy}
              onClick={() => add.mutate(weekday)}
            >
              {name.slice(0, 2)}
            </button>
          );
        })}
      </div>

      <label className="mb-4 grid w-auto gap-1.5">
        <span className="eyebrow">A day turned on applies from</span>
        <input
          type="date"
          className="field w-auto font-mono"
          value={from}
          onChange={(e) => setFrom(e.target.value)}
        />
      </label>

      {rows.length === 0 ? (
        <p className="mb-4 text-sm text-ink-soft">
          No meeting days yet, so no register counts as expected.
        </p>
      ) : (
        <ul className="mb-4">
          {rows.map((row) => (
            <li
              key={row.id}
              className="flex flex-wrap items-center gap-3 border-b border-rule py-1.5 text-sm last:border-0"
            >
              <span className={row.effectiveTo ? 'text-ink-faint' : ''}>
                {WEEKDAYS[row.weekday]}
              </span>
              <span className="ml-auto font-mono text-xs text-ink-soft">
                {row.effectiveTo
                  ? `${row.effectiveFrom} to ${row.effectiveTo}`
                  : `from ${row.effectiveFrom}`}
              </span>
              {row.effectiveTo ? (
                <button
                  type="button"
                  className="font-mono text-xs text-ink-faint underline hover:text-ink"
                  disabled={setEnd.isPending}
                  onClick={() => setEnd.mutate({ id: row.id, date: null })}
                >
                  reopen
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      {add.error ? (
        <div className="mb-4">
          <ErrorNote message={message(add.error, 'Could not add that day.')} />
        </div>
      ) : null}
      {setEnd.error ? (
        <div className="mb-4">
          <ErrorNote message={message(setEnd.error, 'Could not change that day.')} />
        </div>
      ) : null}

      {undo ? (
        <UndoNote
          message={undo.message}
          onUndo={() => {
            setEnd.mutate({ id: undo.id, date: null });
            setUndo(null);
          }}
          onDismiss={() => setUndo(null)}
        />
      ) : null}
    </section>
  );
}

/**
 * The roll, and the students who could join it.
 *
 * An enrolment is a dated record rather than a membership flag, which is what
 * keeps a student on every register they were already marked on after they
 * leave. That is why the roll above is not simply the ticked rows of the list
 * below: ticking adds, but unticking would have to mean "ended, on some date",
 * and no checkbox can say that.
 */
function Roster({ classId }: { classId: string }) {
  const queryClient = useQueryClient();
  const [startDate, setStartDate] = useState(today());
  const [query, setQuery] = useState('');
  const [showEnded, setShowEnded] = useState(false);
  const [undo, setUndo] = useState<{ message: string; id: string } | null>(null);

  const students = useQuery({ queryKey: ['students'], queryFn: api.admin.students });
  const enrollments = useQuery({
    queryKey: ['enrollments', classId],
    queryFn: () => api.admin.enrollments(classId),
  });

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: ['enrollments', classId] });
    await queryClient.invalidateQueries({ queryKey: ['classes'] });
    await queryClient.invalidateQueries({ queryKey: ['grid', classId] });
  };

  const enroll = useMutation({
    mutationFn: (studentIds: string[]) => api.admin.enrollMany({ classId, studentIds, startDate }),
    onSuccess: async () => {
      setQuery('');
      await invalidate();
    },
  });

  const end = useMutation({
    mutationFn: ({ id, date }: { id: string; date: string | null }) =>
      api.admin.endEnrollment(id, date),
    onSuccess: invalidate,
  });

  const enrolled = enrollments.data?.enrollments ?? [];
  const open = enrolled.filter((e) => !e.endDate);
  const openIds = new Set(open.map((e) => e.studentId));
  const endedIds = new Set(enrolled.filter((e) => e.endDate).map((e) => e.studentId));

  /*
   * Deactivated students are left out. GET /admin/students does not filter on
   * isActive and the old menu offered them, but enrolling somebody already
   * marked as having left is almost certainly a slip rather than an intention.
   */
  const candidates = (students.data?.students ?? [])
    .filter((s) => s.isActive && !openIds.has(s.id))
    .filter((s) => showEnded || !endedIds.has(s.id))
    .map((s) => {
      const previous = enrolled.find((e) => e.studentId === s.id && e.endDate);
      return {
        id: s.id,
        label: `${s.lastName}, ${s.firstName}`,
        detail: previous ? `left ${previous.endDate}` : undefined,
        muted: Boolean(previous),
      };
    });

  return (
    <Related
      title="On the roll"
      description="Enrolments are dated, so ending one keeps the student on every register they were already marked on."
      count={`${open.length} on the roll`}
      error={end.error ? message(end.error, 'Could not change that enrolment.') : null}
      undo={
        undo
          ? {
              message: undo.message,
              onUndo: () => {
                end.mutate({ id: undo.id, date: null });
                setUndo(null);
              },
              onDismiss: () => setUndo(null),
            }
          : null
      }
      label="Add to the roll"
      options={candidates}
      query={query}
      onQuery={setQuery}
      empty={
        showEnded
          ? 'Every student is already on the roll.'
          : 'Every student is on the roll, or has left it.'
      }
      searchPlaceholder="surname or first name"
      disabled={enroll.isPending}
      filters={
        <label className="flex items-center gap-2 pb-2 text-sm text-ink-soft">
          <input
            type="checkbox"
            checked={showEnded}
            onChange={(e) => setShowEnded(e.target.checked)}
          />
          include students who have left
        </label>
      }
      verb="Enrol"
      applying={enroll.isPending}
      actions={
        <label className="grid gap-1.5">
          <span className="eyebrow">From</span>
          <input
            type="date"
            className="field w-auto font-mono"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </label>
      }
      note={
        enroll.error ? (
          <div className="mt-3">
            <ErrorNote message={message(enroll.error, 'Could not enrol.')} />
          </div>
        ) : null
      }
      onApply={(ids, done) => {
        setUndo(null);
        enroll.mutate(ids, { onSuccess: done });
      }}
    >
      <ul className="mb-4">
        {enrolled.map((enrollment) => (
          <li
            key={enrollment.id}
            className="flex flex-wrap items-center gap-3 border-b border-rule py-1.5 text-sm last:border-0"
          >
            <span className={enrollment.endDate ? 'text-ink-faint line-through' : ''}>
              {enrollment.lastName}, {enrollment.firstName}
            </span>
            <span className="ml-auto font-mono text-xs text-ink-soft">
              {enrollment.endDate ? `left ${enrollment.endDate}` : `since ${enrollment.startDate}`}
            </span>
            {enrollment.endDate ? null : (
              <Confirm
                trigger="end enrolment"
                triggerClassName="font-mono text-xs text-ink-faint underline hover:text-ink"
                title={`End ${enrollment.firstName} ${enrollment.lastName}'s enrolment?`}
                description="They stay on every register they were already marked on, and leave the ones after the day chosen here."
                body={
                  <label className="grid gap-1.5">
                    <span className="eyebrow">Last day</span>
                    <input
                      name="date"
                      type="date"
                      className="field w-auto font-mono"
                      defaultValue={today()}
                      required
                    />
                  </label>
                }
                confirmLabel="End"
                pendingLabel="Ending…"
                disabled={end.isPending}
                onConfirm={async (form) => {
                  const date = String(form.get('date'));
                  await end.mutateAsync({ id: enrollment.id, date });
                  setUndo({
                    message: `Ended ${enrollment.firstName} ${enrollment.lastName}'s enrolment on ${date}.`,
                    id: enrollment.id,
                  });
                }}
                errorFallback="Could not end that enrolment."
              />
            )}
          </li>
        ))}
        {enrolled.length === 0 ? (
          <li className="py-1.5 text-sm text-ink-soft">Nobody enrolled yet.</li>
        ) : null}
      </ul>
    </Related>
  );
}

/**
 * The class's periods, in the order they appear as columns on the register.
 *
 * Reordering is a drag on the grip. The grip is also the keyboard control:
 * dnd-kit's keyboard sensor lifts with Space, moves with the arrow keys and
 * drops with Space, announcing each step through a live region. That is why
 * there are no separate up and down buttons any more, and why the grip is a
 * real focusable button rather than a decorative glyph.
 *
 * A drop sends the whole reordered list to PATCH /admin/classes/:id, which
 * rewrites every period's sort order from its position in the array.
 */
function PeriodList({
  periods,
  busy,
  onReorder,
  onRemove,
}: {
  periods: ClassDetail['periods'];
  busy: boolean;
  onReorder: (periodIds: string[]) => void;
  /** Asks first; the row's panel waits on the promise. */
  onRemove: (periodId: string) => Promise<unknown>;
}) {
  // Derived straight from props: the optimistic cache update in the parent is
  // what makes a drop hold its position, so this needs no state of its own.
  const order = periods.map((p) => p.periodId);
  const byId = new Map(periods.map((p) => [p.periodId, p]));

  const sensors = useSensors(
    // A small distance so a click on Remove is never read as a drag.
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const from = order.indexOf(String(active.id));
    const to = order.indexOf(String(over.id));
    if (from === -1 || to === -1) return;
    onReorder(arrayMove(order, from, to));
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
      accessibility={{
        announcements: {
          onDragStart: ({ active }) =>
            `Picked up ${byId.get(String(active.id))?.name ?? 'period'}.`,
          onDragOver: ({ active, over }) =>
            over
              ? `${byId.get(String(active.id))?.name ?? 'Period'} is now position ${
                  order.indexOf(String(over.id)) + 1
                } of ${order.length}.`
              : undefined,
          onDragEnd: ({ active, over }) =>
            over
              ? `${byId.get(String(active.id))?.name ?? 'Period'} dropped at position ${
                  order.indexOf(String(over.id)) + 1
                } of ${order.length}.`
              : undefined,
          onDragCancel: ({ active }) =>
            `Reordering cancelled. ${byId.get(String(active.id))?.name ?? 'Period'} is back where it was.`,
        },
      }}
    >
      <SortableContext items={order} strategy={verticalListSortingStrategy}>
        <ol className="mb-4 grid">
          {order.map((periodId, index) => {
            const period = byId.get(periodId);
            if (!period) return null;
            return (
              <PeriodRow
                key={periodId}
                id={periodId}
                index={index}
                name={period.name}
                isRetired={period.isRetired}
                busy={busy}
                onRemove={() => onRemove(periodId)}
              />
            );
          })}
        </ol>
      </SortableContext>
    </DndContext>
  );
}

function PeriodRow({
  id,
  index,
  name,
  isRetired,
  busy,
  onRemove,
}: {
  id: string;
  index: number;
  name: string;
  isRetired: boolean;
  busy: boolean;
  onRemove: () => Promise<unknown>;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className="flex flex-wrap items-center gap-3 border-b border-rule py-2 last:border-0"
      data-dragging={isDragging ? 'true' : undefined}
    >
      <button
        type="button"
        ref={setActivatorNodeRef}
        className="grip"
        aria-label={`Reorder ${name}`}
        disabled={busy}
        {...attributes}
        {...listeners}
      >
        <GripIcon />
      </button>
      <span className="font-mono text-xs text-ink-faint tabular">{index + 1}</span>
      <span className={isRetired ? 'text-ink-faint line-through' : ''}>{name}</span>
      {isRetired ? <span className="eyebrow">retired</span> : null}
      <span className="ml-auto">
        <Confirm
          trigger="Remove"
          title={`Remove ${name} from this class?`}
          description="The column leaves this class's future registers. Registers already marked keep it."
          confirmLabel="Remove"
          pendingLabel="Removing…"
          disabled={busy}
          onConfirm={onRemove}
          errorFallback="Could not remove that period."
        />
      </span>
    </li>
  );
}

/** Two columns of dots: the conventional "this can be dragged" mark. */
function GripIcon() {
  return (
    <svg width="10" height="16" viewBox="0 0 10 16" aria-hidden="true" focusable="false">
      {[3, 8, 13].map((y) => (
        <g key={y}>
          <circle cx="2" cy={y} r="1.2" fill="currentColor" />
          <circle cx="8" cy={y} r="1.2" fill="currentColor" />
        </g>
      ))}
    </svg>
  );
}
